import { useState } from 'react';
import { API_KEY_SCOPES } from '@capforge/shared';
import type { ApiKeyDto, CreateApiKeyResponse } from '@capforge/shared';
import { api, errorMessage } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatDateTime, formatNumber, formatRelative } from '../lib/format';
import { Table } from '../components/Table';
import type { Column } from '../components/Table';
import { Badge } from '../components/Badge';
import { Modal } from '../components/Modal';
import { CopyButton } from '../components/CopyButton';
import { ErrorBox } from '../components/ErrorBox';
import { Spinner } from '../components/Spinner';

type Scope = (typeof API_KEY_SCOPES)[number];

/** Mac dinh 120 req/phut — dung con so mac dinh cua server de dashboard khong noi khac .env. */
const DEFAULT_RATE_LIMIT = 120;

export function Keys() {
  const keys = useAsync((signal) => api.keys(signal), []);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreateApiKeyResponse | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<ApiKeyDto | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const toggleEnabled = (key: ApiKeyDto): void => {
    setBusyId(key.id);
    setRowError(null);
    api.updateKey(key.id, { enabled: !key.enabled }).then(
      () => {
        setBusyId(null);
        keys.reload();
      },
      (err: unknown) => {
        setBusyId(null);
        setRowError(errorMessage(err));
      },
    );
  };

  const revoke = (key: ApiKeyDto): void => {
    setBusyId(key.id);
    setRowError(null);
    api.deleteKey(key.id).then(
      () => {
        setBusyId(null);
        setConfirmRevoke(null);
        keys.reload();
      },
      (err: unknown) => {
        setBusyId(null);
        setConfirmRevoke(null);
        setRowError(errorMessage(err));
      },
    );
  };

  const columns: Column<ApiKeyDto>[] = [
    {
      key: 'label',
      header: 'Project',
      render: (row) => (
        <div>
          <div className="font-medium text-slate-900 dark:text-slate-100">{row.label}</div>
          <div className="cf-mono text-xs text-slate-500 dark:text-slate-400">{row.prefix}…</div>
        </div>
      ),
    },
    {
      key: 'scopes',
      header: 'Scope',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.scopes.map((scope) => (
            <Badge key={scope} tone={scope === 'admin' ? 'warn' : 'neutral'}>
              {scope}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'state',
      header: 'Trạng thái',
      render: (row) => (
        <Badge tone={row.enabled ? 'success' : 'danger'}>
          {row.enabled ? 'đang bật' : 'đã khoá'}
        </Badge>
      ),
    },
    {
      key: 'limits',
      header: 'Giới hạn',
      className: 'whitespace-nowrap',
      render: (row) => (
        <div className="text-xs">
          <div>{formatNumber(row.rateLimitPerMin)} req/phút</div>
          <div className="text-slate-500 dark:text-slate-400">
            {row.dailyQuota === null ? 'không giới hạn/ngày' : `${formatNumber(row.dailyQuota)}/ngay`}
          </div>
        </div>
      ),
    },
    {
      key: 'usage',
      header: 'Thống kê',
      render: (row) => (
        <div className="text-xs leading-5">
          <div>
            <span className="font-medium text-slate-900 dark:text-slate-100">
              {formatNumber(row.usage.total)}
            </span>{' '}
            tổng ·{' '}
            <span className="font-medium text-slate-900 dark:text-slate-100">
              {formatNumber(row.usage.last24h)}
            </span>{' '}
            trong 24h
          </div>
          <div className="text-slate-500 dark:text-slate-400">
            nội bộ {formatNumber(row.usage.localSolved)} · upstream{' '}
            {formatNumber(row.usage.upstreamSolved)} · thất bại {formatNumber(row.usage.failed)}
          </div>
        </div>
      ),
    },
    {
      key: 'time',
      header: 'Lần cuối dùng',
      className: 'whitespace-nowrap',
      render: (row) => (
        <div className="text-xs">
          <div title={formatDateTime(row.lastUsedAt)}>{formatRelative(row.lastUsedAt)}</div>
          <div className="text-slate-500 dark:text-slate-400" title={formatDateTime(row.createdAt)}>
            tao {formatRelative(row.createdAt)}
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right whitespace-nowrap',
      render: (row) => (
        <div className="flex justify-end gap-1">
          {busyId === row.id ? (
            <Spinner />
          ) : (
            <>
              <button
                type="button"
                className="cf-btn cf-btn-ghost"
                onClick={() => toggleEnabled(row)}
              >
                {row.enabled ? 'Tạm khoá' : 'Bật lại'}
              </button>
              <button
                type="button"
                className="cf-btn cf-btn-danger"
                onClick={() => setConfirmRevoke(row)}
              >
                Thu hồi
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">API key</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Mỗi project một key. Task đều gắn apiKeyId nên thống kê ở đây chính là thống kê theo
            project.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="cf-btn cf-btn-ghost" onClick={() => keys.reload()}>
            Lam moi
          </button>
          <button type="button" className="cf-btn cf-btn-primary" onClick={() => setCreating(true)}>
            Tạo key mới
          </button>
        </div>
      </div>

      {rowError !== null && <ErrorBox message={rowError} title="Thao tác thất bại" />}

      <section className="cf-card">
        <Table
          columns={columns}
          rows={keys.data ?? []}
          rowKey={(row) => row.id}
          loading={keys.loading}
          error={keys.error}
          onRetry={keys.reload}
          emptyMessage="Chưa có API key nào"
          emptyHint="Tạo một key cho project đầu tiên, rồi đổi baseUrl của project sang capforge."
        />
      </section>

      <CreateKeyModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(result) => {
          setCreating(false);
          setCreated(result);
          keys.reload();
        }}
      />

      <NewKeyModal result={created} onClose={() => setCreated(null)} />

      <Modal
        open={confirmRevoke !== null}
        title="Thu hồi API key"
        onClose={() => setConfirmRevoke(null)}
        footer={
          <>
            <button
              type="button"
              className="cf-btn cf-btn-ghost"
              onClick={() => setConfirmRevoke(null)}
            >
              Huỷ
            </button>
            <button
              type="button"
              className="cf-btn cf-btn-danger"
              onClick={() => {
                if (confirmRevoke !== null) revoke(confirmRevoke);
              }}
            >
              Thu hồi vĩnh viễn
            </button>
          </>
        }
      >
        <p className="text-sm text-slate-700 dark:text-slate-300">
          Thu hồi key <strong>{confirmRevoke?.label}</strong> (
          <code className="cf-mono">{confirmRevoke?.prefix}…</code>)?
        </p>
        <p className="mt-2 text-sm text-red-700 dark:text-red-300">
          Mọi project đang dùng key này sẽ nhận{' '}
          <code className="cf-mono">ERROR_KEY_DOES_NOT_EXIST</code> ngay lập tức. Nếu chỉ muốn tạm
          dừng thì bấm &quot;Tạm khoá&quot; thay vì thu hồi.
        </p>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form tao key
// ---------------------------------------------------------------------------

interface CreateKeyModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (result: CreateApiKeyResponse) => void;
}

function CreateKeyModal({ open, onClose, onCreated }: CreateKeyModalProps) {
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<Scope[]>(['solve']);
  const [rateLimit, setRateLimit] = useState(String(DEFAULT_RATE_LIMIT));
  const [quotaEnabled, setQuotaEnabled] = useState(false);
  const [quota, setQuota] = useState('10000');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = (): void => {
    setLabel('');
    setScopes(['solve']);
    setRateLimit(String(DEFAULT_RATE_LIMIT));
    setQuotaEnabled(false);
    setQuota('10000');
    setError(null);
  };

  const submit = (): void => {
    const trimmed = label.trim();
    if (trimmed.length === 0) {
      setError('Hãy đặt tên cho key — sau này bạn sẽ nhìn tên này để biết key của project nào.');
      return;
    }
    if (scopes.length === 0) {
      setError('Phải chọn ít nhất một scope.');
      return;
    }
    const rate = Number(rateLimit);
    if (!Number.isInteger(rate) || rate < 1) {
      setError('Rate limit phải là số nguyên dương.');
      return;
    }
    let dailyQuota: number | null = null;
    if (quotaEnabled) {
      const parsed = Number(quota);
      if (!Number.isInteger(parsed) || parsed < 1) {
        setError('Quota ngày phải là số nguyên dương.');
        return;
      }
      dailyQuota = parsed;
    }

    setSaving(true);
    setError(null);
    api.createKey({ label: trimmed, scopes, rateLimitPerMin: rate, dailyQuota }).then(
      (result) => {
        setSaving(false);
        reset();
        onCreated(result);
      },
      (err: unknown) => {
        setSaving(false);
        setError(errorMessage(err));
      },
    );
  };

  return (
    <Modal
      open={open}
      title="Tạo API key mới"
      onClose={() => {
        reset();
        onClose();
      }}
      footer={
        <>
          <button
            type="button"
            className="cf-btn cf-btn-ghost"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Huỷ
          </button>
          <button
            type="button"
            className="cf-btn cf-btn-primary"
            onClick={submit}
            disabled={saving}
          >
            {saving ? <Spinner label="Đang tạo" /> : 'Tạo key'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="cf-label" htmlFor="key-label">
            Ten project
          </label>
          <input
            id="key-label"
            className="cf-input"
            value={label}
            maxLength={120}
            placeholder="vd: scraper-noi-bo"
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>

        <div>
          <span className="cf-label">Scope</span>
          <div className="flex gap-4">
            {API_KEY_SCOPES.map((scope) => (
              <label key={scope} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
                  checked={scopes.includes(scope)}
                  onChange={(event) => {
                    // Doc gia tri NGAY, khong doc trong ham cap nhat state: ham do co the
                    // chay tre hon, luc do su kien khong con dang tin cay.
                    const checked = event.target.checked;
                    setScopes((prev) => {
                      if (checked) {
                        return prev.includes(scope) ? prev : [...prev, scope];
                      }
                      return prev.filter((s) => s !== scope);
                    });
                  }}
                />
                <code className="cf-mono">{scope}</code>
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            <code className="cf-mono">solve</code> đủ cho project gọi captcha. Chỉ cấp{' '}
            <code className="cf-mono">admin</code> cho key bạn dùng để vào dashboard này.
          </p>
        </div>

        <div>
          <label className="cf-label" htmlFor="key-rate">
            Rate limit (request/phut)
          </label>
          <input
            id="key-rate"
            className="cf-input"
            inputMode="numeric"
            value={rateLimit}
            onChange={(event) => setRateLimit(event.target.value)}
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
              checked={quotaEnabled}
              onChange={(event) => setQuotaEnabled(event.target.checked)}
            />
            Giới hạn số request mỗi ngày
          </label>
          {quotaEnabled && (
            <input
              className="cf-input mt-2"
              inputMode="numeric"
              value={quota}
              onChange={(event) => setQuota(event.target.value)}
            />
          )}
        </div>

        {error !== null && (
          <p className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Hop hien key tho — chi mot lan duy nhat
// ---------------------------------------------------------------------------

function NewKeyModal({
  result,
  onClose,
}: {
  result: CreateApiKeyResponse | null;
  onClose: () => void;
}) {
  return (
    <Modal
      open={result !== null}
      title="Key đã được tạo"
      onClose={onClose}
      footer={
        <button type="button" className="cf-btn cf-btn-primary" onClick={onClose}>
          Tôi đã lưu key
        </button>
      }
    >
      {result !== null && (
        <div className="space-y-3">
          <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-3 dark:border-amber-600 dark:bg-amber-950">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Đây là lần duy nhất key thô được hiện.
            </p>
            <p className="mt-1 text-sm text-amber-900 dark:text-amber-200">
              Server chỉ lưu bản băm (hash) của key, không lưu bản thô. Đóng hộp này lại là không
              lấy lại được — lúc đó chỉ còn cách thu hồi và tạo key khác.
            </p>
          </div>

          <div>
            <span className="cf-label">Key</span>
            <div className="flex gap-2">
              <code className="cf-mono block w-full select-all break-all rounded-md border border-slate-300 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-950">
                {result.plaintextKey}
              </code>
              <CopyButton value={result.plaintextKey} />
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-2 text-sm">
            <dt className="text-slate-500 dark:text-slate-400">Tên project</dt>
            <dd>{result.key.label}</dd>
            <dt className="text-slate-500 dark:text-slate-400">Scope</dt>
            <dd className="cf-mono">{result.key.scopes.join(', ')}</dd>
            <dt className="text-slate-500 dark:text-slate-400">Rate limit</dt>
            <dd>{formatNumber(result.key.rateLimitPerMin)} req/phút</dd>
            <dt className="text-slate-500 dark:text-slate-400">Quota ngày</dt>
            <dd>
              {result.key.dailyQuota === null
                ? 'không giới hạn'
                : formatNumber(result.key.dailyQuota)}
            </dd>
          </dl>

          <div>
            <span className="cf-label">Dùng ngay (lớp tương thích 2Captcha)</span>
            <pre className="cf-mono overflow-x-auto rounded-md border border-slate-300 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-950">
              {`curl -s http://localhost:8088/createTask \\
  -H 'Content-Type: application/json' \\
  -d '{"clientKey":"${result.plaintextKey}",
       "task":{"type":"ImageToTextTask","body":"<base64>"}}'`}
            </pre>
          </div>
        </div>
      )}
    </Modal>
  );
}
